
import React, { useState, useMemo } from 'react';
import { useUser } from '@/context/UserContext';
import Layout from '@/components/Layout';
import { FadeIn } from '@/components/Animations';
import { subDays } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { useSymptomData } from '@/hooks/useSymptomData';
import { DashboardHeader } from '@/components/symptom-reporting/DashboardHeader';
import { DashboardControls } from '@/components/symptom-reporting/DashboardControls';
import { VisualizationPanel } from '@/components/symptom-reporting/VisualizationPanel';
import { InsightsPanel } from '@/components/symptom-reporting/InsightsPanel';

const SymptomReporting = () => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState<string>('trends');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  
  // Use custom hook for symptom data management
  const { symptomData, isLoading, filterDataByDateRange } = useSymptomData(user?.id);
  
  // Filter data based on selected date range
  const filteredData = useMemo(() => {
    return filterDataByDateRange(symptomData, dateRange);
  }, [symptomData, dateRange, filterDataByDateRange]);
  
  // Generate visualization data for selected symptoms
  const visualizationData = useMemo(() => {
    if (selectedSymptoms.length === 0) {
      // Default: select first few symptoms if none selected
      const allSymptoms = new Set(
        symptomData.flatMap(item => item.symptoms).filter(Boolean)
      );
      setSelectedSymptoms(Array.from(allSymptoms).slice(0, 3));
      return filteredData;
    }
    
    return filteredData.filter(item => 
      item.symptoms.some((symptom: string) => selectedSymptoms.includes(symptom))
    );
  }, [filteredData, selectedSymptoms, symptomData]);
  
  return (
    <Layout>
      <div className="container py-8 max-w-6xl">
        <FadeIn>
          <DashboardHeader 
            title="Symptom Reporting Dashboard"
            description="Visualize your symptoms over time to better understand your hormonal health journey"
          />
          
          <div className="bg-white rounded-xl shadow-sm border border-hormony-beige/30 p-4 md:p-6">
            <DashboardControls 
              symptomData={symptomData}
              selectedSymptoms={selectedSymptoms}
              setSelectedSymptoms={setSelectedSymptoms}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
            
            <VisualizationPanel 
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              isLoading={isLoading}
              visualizationData={visualizationData}
              selectedSymptoms={selectedSymptoms}
            />
          </div>
          
          <InsightsPanel 
            symptomData={visualizationData}
            selectedSymptoms={selectedSymptoms}
          />
        </FadeIn>
      </div>
    </Layout>
  );
};

export default SymptomReporting;
